/** A student's words in the person visit: standing, the record's facts, status and the academic record. */
export const studentCopy = {
  standingLabel: "Academic standing",
  standingJob: "This student's cumulative GPA, certified credits and standing.",
  cumulativeGpa: "Cumulative GPA",
  certifiedCredits: "Certified credits",
  provisional: "Provisional: a credit course is still without a counted result.",
  academicUnavailable: "Couldn't read the academic record",
  standingUnavailableDetail: "Neither the GPA nor the standing is drawn. This is not an empty record.",
  academicUnavailableDetail: "Academic standing and courses are unavailable. This is not an empty record.",

  studentLabel: "Student record",
  studentId: "Student ID",
  studentStatus: "Student status",
  placementLabel: "Placement",
  editPlacement: "Edit placement",
  placementDetail: "Program and cohort are saved independently.",
  contactLabel: "Contact details",
  contactAndIdentity: "Contact and identity",
  personalLabel: "Personal details",
  personalAndNotes: "Personal details and notes",
  personalRecorded: "Personal information recorded",
  noteRecorded: "Registrar note recorded. Only registrars see it.",
  nothingRecorded: "Nothing recorded",
  noExtraTime: "No extra time recorded",
  issuanceLabel: "Student ID history",
  issuanceUnavailable: "Issuance details are unavailable.",
  recordHistory: "Record history",
  sourceAndIssuance: "Source records and Student ID issuance",
  issuanceSummary: "Student ID issuance",
  placeInCourse: "Place in a course",
  noInvitation: "No invitation yet.",
  studentJob: "This person's student record: placement and contact, saved where each is drawn.",
  program: "Program",
  cohort: "Cohort",
  gender: "Gender",
  phone: "Phone",
  city: "City",
  country: "Country",
  address: "Address",
  emergencyContact: "Emergency contact",
  citizenshipNote: "Citizenship note",
  registrarNote: "Registrar note",
  registrarNoteScope: "Only registrars see this note.",
  unassigned: "Unassigned",
  thisProgram: "This record's program",
  thisCohort: "This record's cohort",
  anotherProgram: "Another program",
  anotherCohort: "Another cohort",
  issuedOutsideSequence: "Student ID kept exactly as issued, outside the standard sequence.",

  statusLabel: "Change student status",
  restoreLabel: "Restore student",
  statusJob: "Change this student's status, with the reason on record.",
  statusDetail: "Course enrollments, cohort and account access stay as they are.",
  restoreDetail: "Restores the student record. Sign-in access and course enrollments stay as they are.",
  changeStatus: "Change status",
  newStatus: "New status",
  chooseStatusOption: "Choose a status",
  chooseStatus: "Choose the new status.",
  whyStatus: "Why is the student status changing?",
  sayWhy: "Say why the status is changing.",
  reasonNeeded: "Reason needed",
  readyToChange: "Ready to change",
  statusCap: "When you change",
  restoreCap: "When you restore",
  withdrawVerb: "Withdraw student",
  statusKeeps: "Course enrollments, cohort and account access stay as they are",
  withdrawEffect: "Withdraw the student",
  withdrawCloses: "Withdraw current course participation and revoke pending student invitations; completed and historical work stays",
  withdrawSuspends: "Keep sign-in access, teaching responsibilities and Google accounts as they are",
  restoreEffect: "Restore the student record; sign-in access stays as it is",
  enrollmentsNotReinstated: "Course enrollments are not reinstated",
  statusRefused: "Not changed.",
  statusUnknown: "Changing the student status",
  unknownSentence: "We could not confirm whether this was recorded. Check before trying again.",
  checkNote: "Reads the student record. Nothing is changed again.",

  correctLabel: "Correct conversion",
  correctJob: "Return a mistaken admission to its application, keeping the Student ID.",
  correctDetail: "For a student admitted by mistake.",
  admitted: "Admitted",
  readinessUnavailable: "Couldn't read the admission",
  returnApplicationTo: "Return the application to",
  whyCorrect: "Why is this conversion being corrected?",
  sayWhyCorrect: "Say why the conversion is being corrected.",
  readyToCorrect: "Ready to correct",
  correctVerb: "Correct conversion",
  correctCap: "When you correct",
  correctWithdraws: "Withdraw the student record",
  correctKeepsSid: "The Student ID is kept for a future admission",
  correctRefused: "Not corrected.",
  correctUnknown: "Correcting the conversion",

  academicLabel: "Academic record",
  academicJob: "The courses this person registered for, each with its marks and attendance.",
  noCoursesYet: "No courses yet",
  noTerm: "No term assigned",
  noInstructor: "No instructor recorded",
  noCredits: "No credits recorded",
  withdrawn: "Withdrawn",
  audit: "Audit",
  awaitingCertification: "Awaiting certification",
  inProgress: "In progress",
  currentTerm: "Current term",
  certified: "Certified",
  noGpaCredits: "No GPA credits",
  marksAndAttendance: "Marks and attendance",
  enrollment: "Enrollment",
  enrollJob: "One learner's attendance marks and result in one course, read where the course keeps them.",
  marksUnavailable: "Couldn't read marks and attendance",
  marksUnavailableDetail: "No grade or attendance is drawn. This is not a missing mark.",
  notOnRoster: "They aren't on this course's roster now, so no marks are drawn.",

  provenanceJob: "Where this student record came from: each source, then each receipt.",
  rosterLabel: "Recorded from a roster",
  importedLabel: "Imported history",
  provenanceDetail: "Source identity and receipts.",
  correctsReceipt: "Corrects an earlier receipt",
  openFirstBatch: "Open first import batch",
  openReceiptBatch: "Open its batch",
} as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** An import receipt's action in words (lms_import_receipt.action). */
const IMPORT_ACTION: Partial<Record<string, string>> = {
  created: "created this record",
  would_create: "would create this record",
  matched: "matched this record",
  corrected: "corrected this record",
  skipped: "skipped",
  rejected: "rejected",
};

export const studentText = {
  hours: (start: string, end: string, zone: string | null) => `${start} to ${end}${zone ? ` ${zone}` : ""}`,
  credits: (credits: number) => plural(credits, "credit", "credits"),
  termGpa: (gpa: string, provisional: boolean) => `Term GPA ${gpa}${provisional ? " · Provisional" : ""}`,
  standing: (label: string, floor: string) => `${label}. Good standing is a cumulative GPA of ${floor} or above.`,
  archived: (label: string) => `${label} (archived)`,
  sidAndStatus: (sid: string, status: string) => `SID ${sid} · ${status}`,
  issued: (day: string, year: number | string, source: string) => `Student ID issued ${day} for the ${year} cohort, from ${source}.`,
  becomes: (status: string) => `Student status becomes ${status}`,
  fromTo: (from: string, to: string) => `${from} to ${to}`,
  returnTo: (stage: string) => `Return the application to ${stage}`,
  yearsRead: (years: string[]) => (years.length ? `Academic years ${years.join(", ")}` : "No academic years yet"),
  coursesAcross: (courses: number, years: number) => `${plural(courses, "course", "courses")} across ${plural(years, "academic year", "academic years")}`,
  term: (year: string, term: string) => `${year} · ${term}`,
  course: (code: string, title: string) => `${code} · ${title}`,
  enrollSubject: (name: string, course: string) => `${name} in ${course}`,
  attended: (held: number) => `of ${held} attended`,
  marksReadOnly: (result: string) => `${result}. Read-only here: marks are entered in the course.`,
  result: (letter: string) => `Result ${letter}`,
  provenanceCount: (sources: number, receipts: number, roster: boolean) =>
    `${plural(sources, roster ? "roster" : "source", roster ? "rosters" : "sources")}, ${plural(receipts, "receipt", "receipts")}`,
  recordedFromRoster: (source: string, day: string) => `Recorded from registrar roster ${source} on ${day}`,
  receipt: (dryRun: boolean, action: string, day: string) => `${dryRun ? "Dry run" : "Applied"}: ${IMPORT_ACTION[action] ?? action.replaceAll("_", " ")}, ${day}`,
  sourceKey: (roster: boolean, key: string, day: string) => (roster ? `Roster key ${key}` : `First imported ${day} · Source key ${key}`),
};
